import { useEffect, useRef, useState } from 'react';
import {
  createTheme,
  ThemeProvider,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import { clearError } from '../../store/slices/authSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import logo from '../../assets/images/cacxi-white-logo.svg';
import Notifications from '../common/Notifications';
import { confirmNewPassword, forgetPassword, logoutUser } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';
import { useDispatch } from 'react-redux';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useForm } from 'react-hook-form';
import { UserPlus, KeyRound, ArrowLeft, Check, X } from 'lucide-react';

/**
 * Account Setup Form - Used for new user/dealer activation
 * Similar to ForgetPasswordForm but with account setup messaging
 */
const AccountSetupForm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get email from URL query params (for activation links)
  const emailFromUrl = searchParams.get('email') || '';

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const lightTheme = createTheme({
    palette: {
      mode: 'dark',
    },
  });

  const { register, handleSubmit, watch, reset, getValues } = useForm({
    defaultValues: {
      email: emailFromUrl,
      code: '',
      newPassword: '',
      confirmPassword: '',
      otp: Array(6).fill(''),
    },
  });

  const inputRefs = useRef<HTMLInputElement[]>([]);
  const confirmPasswordRef = useRef<HTMLInputElement | null>(null);
  const [requestCode, setRequestCode] = useState(true);
  const [showCodeConfirm, setShowCodeConfirm] = useState(false);
  const newPassword = watch('newPassword');
  const confirmPassword = watch('confirmPassword');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });
  const [timer, setTimer] = useState(60);
  const [showResendButton, setShowResendButton] = useState(false);

  const isOtpValid = watch('otp').every((digit: string) => digit.length === 1);

  const passwordChecks = {
    hasUpper: /[A-Z]/.test(newPassword),
    hasLower: /[a-z]/.test(newPassword),
    hasNumber: /\d/.test(newPassword),
    hasSpecial: /[!@#$%^&*()_\-+=<>?{}[\]~]/.test(newPassword),
    hasMinLength: newPassword?.length >= 8,
    passwordsMatch: newPassword === confirmPassword && !!confirmPassword,
  };

  const passwordsValid =
    passwordChecks.hasUpper &&
    passwordChecks.hasLower &&
    passwordChecks.hasNumber &&
    passwordChecks.hasSpecial &&
    passwordChecks.hasMinLength &&
    passwordChecks.passwordsMatch;

  const isFormValid = isOtpValid && passwordsValid;

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (isSubmitting) return;
    const target = e.target as HTMLInputElement;
    if (e.key === 'Backspace' && !target.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (target.value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (isSubmitting) return;
    const pasteData = e.clipboardData.getData('text');
    const otpValues = pasteData.split('').slice(0, inputRefs.current.length);
    otpValues.forEach((value, index) => {
      if (inputRefs.current[index]) {
        inputRefs.current[index].value = value;
        inputRefs.current[index]?.focus();
      }
    });
    inputRefs.current[otpValues.length - 1]?.focus();
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setShowResendButton(true);
    }
  }, [timer]);

  const handleRequestCode = async (data: any) => {
    try {
      await dispatch(forgetPassword(data.email)).unwrap();
      setRequestCode(false);
      setShowCodeConfirm(true);
      setNotification({
        open: true,
        message: 'Verification code sent to your email',
        severity: SEVERITY.SUCCESS,
      });
      reset({ ...data });
    } catch (error: any) {
      setRequestCode(true);
      setShowCodeConfirm(false);
      setError(error.message || 'Failed to send verification code');
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    }
  };

  const resendRequestCode = async () => {
    setTimer(60);
    setShowResendButton(false);
    try {
      await dispatch(forgetPassword(getValues('email'))).unwrap();
      setNotification({
        open: true,
        message: 'Verification code sent to your email',
        severity: SEVERITY.SUCCESS,
      });
    } catch (error: any) {
      setError(error.message || 'Failed to send verification code');
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handlePasswordChange = async (data: any) => {
    confirmPasswordRef.current?.focus();
    setIsSubmitting(true);
    try {
      const otpValue = data.otp.join('');
      const requestData = {
        email: data.email,
        newPassword: data.newPassword,
        code: otpValue,
      };
      await dispatch(confirmNewPassword(requestData)).unwrap();
      await dispatch(logoutUser()).unwrap();
      setNotification({
        open: true,
        message: 'Account activated successfully! You can now login.',
        severity: SEVERITY.SUCCESS,
      });
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message,
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
      '&.Mui-focused': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      },
    },
  };

  const PasswordRequirementChip = ({ valid, label }: { valid: boolean; label: string }) => (
    <Chip
      size="small"
      icon={valid ? <Check size={14} /> : <X size={14} />}
      label={label}
      sx={{
        fontSize: '0.7rem',
        height: '24px',
        backgroundColor: valid ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255, 255, 255, 0.05)',
        color: valid ? '#4ade80' : 'rgba(255, 255, 255, 0.5)',
        border: valid ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
        '& .MuiChip-icon': {
          color: valid ? '#4ade80' : 'rgba(255, 255, 255, 0.4)',
        },
      }}
    />
  );

  return (
    <Box className="h-screen flex items-center justify-center font-sans login-bg">
      <div className="w-full max-w-7xl flex flex-col lg:flex-row h-screen">
        {/* Left: Branding (hidden on mobile) */}
        <div className="hidden lg:flex w-full lg:w-1/2 flex-col justify-center items-center">
          <div className="text-white lg:p-16 flex flex-col justify-center items-center lg:items-start z-10 w-full max-w-xl">
            <img className="text-white w-36 mb-2" src={logo} alt="CAC_logo" />
            <div className="hidden lg:flex flex-wrap">
              <Typography className="text-3xl lg:text-4xl font-bold leading-[3rem]">
                Smarter Solutions,
              </Typography>
              <Typography className="text-3xl lg:text-4xl font-bold leading-[3rem]">
                Seamless Growth
              </Typography>
            </div>
            <Typography className="hidden lg:block text-sm lg:text-lg font-normal mt-2">
              Revolutionize Bookings and boost Marketing with our AI powered smart tool
            </Typography>
          </div>
        </div>

        {/* Right: Form Card */}
        <Paper
          className="lg:grow h-full xs:p-4 sm:p-8 lg:p-10 xl:p-6 rounded-md bg-transparent flex flex-col justify-center items-center"
          elevation={0}
        >
          <div className="max-w-sm w-full">
            <ThemeProvider theme={lightTheme}>
              <Box
                sx={{
                  backgroundColor: '#21172B',
                  border: '1px solid #291c35',
                  borderRadius: '12px',
                  px: 5,
                  py: 4,
                }}
              >
                {/* Step 1: Email Verification */}
                {requestCode && (
                  <>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <img
                        className="block lg:hidden text-white w-28 mb-6 mx-auto"
                        src={logo}
                        alt="CAC_logo"
                      />
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(168, 85, 247, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}
                      >
                        <UserPlus size={28} color="#a855f7" />
                      </Box>
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Welcome to CACXI!
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Set up your password to activate your account
                      </Typography>
                    </Box>

                    <form onSubmit={handleSubmit(handleRequestCode)}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        required
                        autoComplete="email"
                        autoFocus={!emailFromUrl}
                        disabled={!!emailFromUrl}
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Enter Valid Email',
                          },
                        })}
                        sx={{
                          ...inputSx,
                          '& .Mui-disabled': {
                            WebkitTextFillColor: 'rgba(255, 255, 255, 0.7)',
                          },
                        }}
                      />

                      {error && (
                        <Alert
                          severity="error"
                          sx={{
                            mt: 2,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#fca5a5',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                          }}
                        >
                          {error}
                        </Alert>
                      )}

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        className="mt-4 bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                        sx={{
                          textTransform: 'none',
                          borderRadius: '12px',
                          py: 1.5,
                          mt: 3,
                        }}
                      >
                        Get Verification Code
                      </Button>
                    </form>
                  </>
                )}

                {/* Step 2: Code + Password Setup */}
                {showCodeConfirm && (
                  <>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <img
                        className="block lg:hidden text-white w-28 mb-6 mx-auto"
                        src={logo}
                        alt="CAC_logo"
                      />
                      {/* Step indicator */}
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                          textTransform: 'uppercase',
                          letterSpacing: 1,
                          mb: 2,
                        }}
                      >
                        Step 2 of 2
                      </Typography>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(168, 85, 247, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}
                      >
                        <KeyRound size={28} color="#a855f7" />
                      </Box>
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          mb: 1,
                        }}
                      >
                        Set Your Password
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        Enter the code we sent to your email
                      </Typography>
                    </Box>

                    <form onSubmit={handleSubmit(handlePasswordChange)}>
                      {/* OTP Input */}
                      <Typography
                        sx={{
                          fontSize: '0.813rem',
                          fontWeight: 600,
                          color: 'rgba(255, 255, 255, 0.8)',
                          mb: 1,
                        }}
                      >
                        Verification Code
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        {Array.from({ length: 6 }, (_, index) => (
                          <TextField
                            key={index}
                            inputRef={el => (inputRefs.current[index] = el)}
                            inputProps={{
                              maxLength: 1,
                              style: { textAlign: 'center', fontSize: '1.25rem' },
                            }}
                            {...register(`otp.${index}`, {
                              required: 'Verification code cannot be empty',
                            })}
                            onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
                              handleKeyUp(e, index)
                            }
                            onPaste={handlePaste}
                            sx={{
                              width: '44px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                                '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
                              },
                              '& .MuiInputBase-input': { padding: '12px 8px' },
                            }}
                          />
                        ))}
                      </Box>

                      {/* Resend Timer */}
                      <Box sx={{ mt: 1.5, mb: 3 }}>
                        {showResendButton ? (
                          <Button
                            onClick={resendRequestCode}
                            sx={{
                              fontSize: '0.813rem',
                              color: '#a855f7',
                              textTransform: 'none',
                              padding: 0,
                              minWidth: 'auto',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                color: '#c084fc',
                              },
                            }}
                          >
                            Resend Code
                          </Button>
                        ) : (
                          <Typography
                            sx={{ fontSize: '0.813rem', color: 'rgba(255, 255, 255, 0.5)' }}
                          >
                            Resend in {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
                          </Typography>
                        )}
                      </Box>

                      {/* Password Fields */}
                      <Typography
                        sx={{
                          fontSize: '0.813rem',
                          fontWeight: 600,
                          color: 'rgba(255, 255, 255, 0.8)',
                          mb: 1,
                        }}
                      >
                        Create Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter your password"
                        type="password"
                        {...register('newPassword', {
                          required: 'Password is required',
                          pattern: {
                            value:
                              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_\-+=<>?{}[\]~]).{8,}$/,
                            message: 'Password does not meet requirements',
                          },
                        })}
                        sx={inputSx}
                      />

                      {/* Password Requirements - Compact Chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5, mb: 2 }}>
                        <PasswordRequirementChip
                          valid={passwordChecks.hasUpper}
                          label="Uppercase"
                        />
                        <PasswordRequirementChip
                          valid={passwordChecks.hasLower}
                          label="Lowercase"
                        />
                        <PasswordRequirementChip valid={passwordChecks.hasNumber} label="Number" />
                        <PasswordRequirementChip
                          valid={passwordChecks.hasSpecial}
                          label="Special"
                        />
                        <PasswordRequirementChip
                          valid={passwordChecks.hasMinLength}
                          label="8+ chars"
                        />
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Confirm your password"
                        type="password"
                        inputRef={confirmPasswordRef}
                        required
                        {...register('confirmPassword', {
                          required: 'Confirm password is required',
                          validate: value => value === newPassword || 'Passwords do not match',
                        })}
                        sx={{ ...inputSx, mb: 1 }}
                      />

                      {confirmPassword && (
                        <Box sx={{ mb: 2 }}>
                          <PasswordRequirementChip
                            valid={passwordChecks.passwordsMatch}
                            label="Passwords match"
                          />
                        </Box>
                      )}

                      {error && (
                        <Alert
                          severity="error"
                          sx={{
                            mb: 2,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            color: '#fca5a5',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                          }}
                        >
                          {error}
                        </Alert>
                      )}

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!isFormValid || isSubmitting}
                        className="bg-purple-800 py-2 text-md text-white font-bold hover:bg-purple-900"
                        sx={{
                          textTransform: 'none',
                          borderRadius: '12px',
                          py: 1.5,
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(168, 85, 247, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)',
                          },
                        }}
                      >
                        {isSubmitting ? 'Activating Account...' : 'Activate Account'}
                      </Button>
                    </form>
                  </>
                )}

                {/* Back to Login */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    onClick={() => navigate('/')}
                    startIcon={<ArrowLeft size={16} />}
                    sx={{
                      fontSize: '0.875rem',
                      color: '#a855f7',
                      textTransform: 'none',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        color: '#c084fc',
                      },
                    }}
                  >
                    Back to Login
                  </Button>
                </Box>
              </Box>
            </ThemeProvider>
          </div>
        </Paper>
      </div>

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default AccountSetupForm;
